import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  GridOptions,
  ITooltipParams,
  SortChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { ExternalTransaction, TranCategory } from '@helios/common-api'
import { BlankSessionService } from '@helios/sessions'
import {
  RootState,
  selectActiveSessionIdentifier,
  selectActualReconDate,
  selectLegacyUserId,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { compareAsc, parseISO } from 'date-fns'
import { Observable, Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'
import {
  BulkDerivedRequestEntry,
  BulkLoadRow,
  loadField,
} from '../../../model/bulk-transaction/bulk-load/bulk-load.model'
import { BulkTransactionModalInput } from '../../../model/bulk-transaction/bulk-popup-input-model'
import {
  BulkSecurityTransferRow,
  securityField,
} from '../../../model/bulk-transaction/bulk-security-transfer/bulk-security-transfer.model'
import {
  AdditionalDetailsSecurityTransfer,
  BulkLoadTransaction,
  BulkLoadTransactionDerived,
  ClearwaterTransactionDetailResponse,
  MetaDataTransaction,
  TransactionRequestWrapper,
} from '../../../model/bulk-transaction/bulk-transaction'
import { BulkTransactionRequest } from '../../../model/bulk-transaction/bulk-transaction.model'
import { BulkLoadValidationView } from '../../../model/bulk-transaction/bulk-validation-view.model'
import { CounterParty } from '../../../model/counter-party.model'
import { Currency } from '../../../model/currency.model'
import { InventoryType } from '../../../model/inventory-type.model'
import { Basis } from '../../../model/mvt-basis.model'
import { SecurityMasterData } from '../../../model/security.model'
import { amortOverrideTypesVal } from '../../../model/single-transaction/amort-override-types'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import {
  TpiPropertiesNumeric,
  TpiPropertiesString,
  TransferPurchaseInfo,
} from '../../../model/single-transaction/security-transfer/transfer-purchase-info.model'
import { Transaction } from '../../../model/single-transaction/transaction'
import { TranCategoryUrl } from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  RequestMode,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { BulkDeleteService } from '../../../services/bulk-transaction/bulk-delete.service'
import { PasteService } from '../../../services/bulk-transaction/paste.service'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import {
  BULK_DIALOG_EVENT_NAME,
  bulkMetricsDetail,
  customSortComparatorBulk,
  customSortComparatorBulkNumeric,
  customSortComparatorBulkValidatorColumn,
  fillHandleDoubleClickHandler,
  onSortChange,
} from '../../../util/bulk-specific-common-utils'
import { mapLoadToBulkTransaction } from '../../../util/bulk-transaction-wrapper-util'
import {
  activateDialog,
  deduceValidityStatusCellClass,
  emptySelectedCellsOnValidKeyPress,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  isDialogComponentInActive,
  markRowsMissingAccount,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import {
  createBulkLoadBasisLoanFieldColumns,
  createBulkLoadLoanFieldColumns,
  getLoanFieldValues,
} from '../../../util/loan-field.util'
import { BulkTransactionModalResponse } from '../bulk-transaction-modal-response'
import { BulkTransactionsSaveConfirmationModalComponent } from '../bulk-transactions-confirm-button-modal/bulk-transactions-confirm-button-modal.component'
import { ErrorTooltipComponent } from '../error-Tooltip/error-tooltip.component'

@Component({
  selector: 'hs-bulk-load-transfer',
  templateUrl: './bulk-load.component.html',
  styleUrls: ['./bulk-load.component.scss'],
  providers: [PasteService, CopyService, BulkDeleteService],
})
export class BulkLoadComponent implements OnInit, OnDestroy {
  @Input() transInput?: BulkTransactionModalInput
  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    readonly store: Store<RootState>,
    readonly modalService: NgbModal,
    public bulkTransactionService: CreateEditTransactionService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly blankSessionService: BlankSessionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly bulkDeleteService: BulkDeleteService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly telemetryService: TelemetryService,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.store
      .select(selectActualReconDate)
      .pipe(take(1))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })
    this.store
      .select(selectLegacyUserId)
      .pipe(take(1))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
    this.userPreferencesService
      .getBulkDialogSortPromptPreference()
      .pipe(take(1))
      .subscribe(sortable => {
        this.sortable = sortable
        if (this.gridOptions.defaultColDef) {
          this.gridOptions.defaultColDef.sortable = sortable
          this.gridOptions.defaultColDef.comparator = customSortComparatorBulk
        }
      })
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
  }

  sortable = false
  rowData: (BulkLoadRow & unknown)[] = []
  currencyList: Currency[] = []
  getFieldValidatorFormatter = getFieldValidatorFormatter
  colDefs = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()
  isDataValid = true
  isValidating = false
  isCommonValidationError = false
  disableSaveValidRows = false
  isDeriving = false
  formSubmitting = false
  allDataValidated = false
  gridApi?: AgGridApi
  note = new FormControl('')
  private readonly destroySubject$ = new Subject<void>()
  securityDetailsShow = new FormControl(true)
  reconDate?: Date
  sessionId?: string
  private userId?: number
  counterPartyList: CounterParty[] = []
  cellLevelErrorMessageMap = new Map<string, string[]>()
  cellLevelWarningMessageMap = new Map<string, string[]>()
  noteWarning: ValidationErrors | null = null
  currentNoteError: ValidationErrors | null =
    TransactionConstants.DEFAULT_NOTE_VALIDATION_ERROR
  genericErrors: string[] = []
  genericWarnings: string[] = []
  loadingDialog = false
  title = 'Load Bulk Edit'
  currentRowIndex = 0
  initialClientLotIdMap: Map<string, string> = new Map()
  originalTransactions: BulkLoadTransaction[] = []
  externalTransaction: ExternalTransaction[] = []
  bulkExternalTransactionDetail: ExternalUnmatchedTransaction[] = []
  isEdit = false
  securityMasterData: Map<string, SecurityMasterData> = new Map<
    string,
    SecurityMasterData
  >()
  clientLotIdMapBulkPanel: Map<string, string> = new Map<string, string>()
  initialBasisIndexMap: Map<string, number> = new Map<string, number>()
  removedBasisIndex: Set<number> = new Set<number>()
  clearwaterTransactionDetail: { [s: string]: Transaction } = {}
  tpiMapByTransaction: {
    [key: string]: { [key: string]: TransferPurchaseInfo }
  } = {}

  basisMap: { [key: number]: Basis[] } = {}
  isCollateralMap: { [key: number]: boolean } = {}
  basisTypeValues: Basis[] = []
  selectedBasisPrefix = new FormControl([])
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  errorDetails: unknown
  showConfirmButtonModal = false
  activeInstances: NgbModalRef[] = []
  compId = 'bulk-load' + Math.random()

  async ngOnInit() {
    const startTime = Date.now()

    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.setNoteErrors()
    })
    this.securityDetailsShow.valueChanges.subscribe((value: boolean) => {
      this.onSecurityDetailsSelectionChange(value)
    })
    this.bulkTransactionService
      .loadCounterParties()
      .subscribe(counterParties => {
        this.counterPartyList = counterParties
      })
    this.colDefs = this.createColumnDefs()
    try {
      await this.init()
    } catch (error) {
      this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
      this.errorDetails = error.error
    }
    this.note.markAsTouched()

    this.note.setErrors(this.currentNoteError)

    const now = Date.now()
    const [count, type] = bulkMetricsDetail(
      this.rowData.length,
      this.transInput,
    )
    this.telemetryService.emitCustomEvent(BULK_DIALOG_EVENT_NAME, {
      dialogName: 'Initial-Load-' + type,
      durationMs: now - startTime,
      tranCount: count,
    })
  }

  onSecurityDetailsSelectionChange(value: boolean) {
    if (value) {
      this.colDefs.splice(4, 0, this.getSecurityDetailsColumnDef())
    } else {
      const index = this.colDefs.findIndex(
        colDef => colDef.headerName === 'Security Details',
      )
      this.colDefs.splice(index, 1)
    }
    const groupState = this.gridApi?.columnApi.getColumnGroupState()

    this.gridApi?.api.setColumnDefs(this.colDefs)

    if (groupState) this.gridApi?.columnApi.setColumnGroupState(groupState)
  }

  private async init() {
    this.loadingDialog = true
    this.title = 'Load Bulk Edit'
    this.isEdit = true
    this.pasteService.setIsEdit()

    if (this.transInput?.cwTranId)
      await this.loadTransactionDetailBulk(
        this.transInput?.cwTranId,
        this.transInput.securityId ?? [],
      )

    if (this.transInput?.cwTranId) {
      this.transInput.transactions = mapLoadToBulkTransaction(
        this.clearwaterTransactionDetail,
      )
    }
    this.originalTransactions = this.transInput
      ?.transactions as BulkLoadTransaction[]
    if (!this.transInput?.cwTranId) {
      await this.loadAdditionalDetailBulk(
        this.originalTransactions.map(
          origTran => origTran.legacyOrigTranId ?? 0,
        ),
        this.originalTransactions.map(origTran => origTran.legacyTranId ?? 0),
        this.originalTransactions.map(origTran => origTran.securityId ?? 0),
        [
          ...new Set(
            this.originalTransactions.map(origTran => origTran.accountId),
          ),
        ],
      )
    }
    await this.loadDataEdit(
      new Set(this.originalTransactions.map(origTran => origTran.accountId)),
    )

    this.originalTransactions.forEach((loadTrans, index) => {
      const bulkLoadWithoutTpi: BulkLoadRow = this.generateDataEntryFromLoad(
        loadTrans,
        index,
      )

      const basisWiseTpiDataForTransaction =
        this.getBasisWiseTpiDataForTransaction(
          this.transInput?.cwTranId
            ? loadTrans.transactionId ?? ''
            : loadTrans.legacyTranId?.toString() ?? '',
        )
      this.rowData.push({
        ...bulkLoadWithoutTpi,
        ...basisWiseTpiDataForTransaction,
      })

      this.initialClientLotIdMap.set(
        bulkLoadWithoutTpi.transactionId,
        bulkLoadWithoutTpi.clientLotId ?? '',
      )
    })
    this.gridApi?.api.applyTransaction({ add: this.rowData })

    this.loadingDialog = false
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getBasisWiseTpiDataForTransaction(tranId: string): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tranRowWithBasis: any = {}

    const basisWiseTpi = this.tpiMapByTransaction[tranId]
    if (basisWiseTpi) {
      const basisWiseTpiMap = new Map(Object.entries(basisWiseTpi))
      basisWiseTpiMap.forEach((value: TransferPurchaseInfo, key: string) => {
        const basisPrefix =
          this.basisTypeValues.find(basis => basis.basisId == Number(key))
            ?.prefix ?? ''
        let property: keyof typeof value
        for (property in value) {
          tranRowWithBasis[
            this.concatToCamelCase(
              property == 'taxDaysHeld' ? '' : basisPrefix,
              property,
            )
          ] = value[property]
        }
      })
    }
    return tranRowWithBasis
  }

  generateDataEntryFromLoad(
    cwTransaction: BulkLoadTransaction,
    index: number,
  ): BulkLoadRow {
    const factor = cwTransaction.factor ?? 1.0
    const transferPrice = cwTransaction.transferPrice ?? 0
    const securityDetails = this.securityMasterData.get(
      cwTransaction.securityId + '',
    )
    const clientLotId =
      this.clientLotIdMapBulkPanel.get(cwTransaction.legacyOrigTranId + '') ??
      ''
    return {
      legacyOrigTranId: cwTransaction.legacyOrigTranId ?? -1,
      transactionId: cwTransaction.transactionId ?? '',
      legacyTranId: cwTransaction.legacyTranId ?? -1,
      rowId: (index + 1).toString(),
      accountId: cwTransaction.accountId,
      tranType: 'LOAD',
      units: cwTransaction.units ?? 0,
      currentUnits: '',
      entryDate: cwTransaction.entryDate,
      clearedDate: cwTransaction.clearedDate,
      assetId: securityDetails?.cusip ?? '',
      securityId: cwTransaction.securityId ?? 0,
      factor: factor,
      currencyCode: securityDetails?.currencyCode ?? 'USD',
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      smType: securityDetails?.typeShortName ?? '',
      marketValue: '',
      transferPrice: transferPrice,
      issueDate: securityDetails?.issueDate
        ? securityDetails.issueDate.split('T')[0]
        : '',
      maturityDate: securityDetails?.maturityDate
        ? securityDetails.maturityDate.split('T')[0]
        : '',
      ticker: securityDetails?.ticker,
      figi: securityDetails?.figi,
      isin: securityDetails?.isin,
      sedol: securityDetails?.sedol,
      counterPartyId: cwTransaction.counterPartyId ?? 13,
      inventoryType: cwTransaction.inventoryType
        ? InventoryType[
            cwTransaction.inventoryType as keyof typeof InventoryType
          ]
        : InventoryType.LONG,
      clientLotId: cwTransaction.clientLotId ?? clientLotId,
      collateralBroker:
        (cwTransaction.dataMap?.collateralBroker as string) ?? '',
      prepaidInterest: (cwTransaction.dataMap?.prepaidInterest as number) ?? 0,
      interestDue: (cwTransaction.dataMap?.interestDue as number) ?? 0,
      interestBearingInterestReserve:
        (cwTransaction.dataMap?.interestBearingInterestReserve as number) ?? 0,
      interestBearingPrincipalReserve:
        (cwTransaction.dataMap?.interestBearingPrincipalReserve as number) ?? 0,
      nonInterestBearingInterestReserve:
        (cwTransaction.dataMap?.nonInterestBearingInterestReserve as number) ??
        0,
      nonInterestBearingPrincipalReserve:
        (cwTransaction.dataMap?.nonInterestBearingPrincipalReserve as number) ??
        0,
      orderId: cwTransaction.orderId ?? '',
      transactionBasis: cwTransaction.transactionBasis,
    }
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  /** @internal */
  async loadDataEdit(accounts: Set<number>) {
    const basisObservable = await this.bulkTransactionService
      .loadBasisForAccounts(Array.from(accounts))
      .toPromise()
    const collateralObservable = await this.bulkTransactionService
      .isCollateralBroker(Array.from(accounts))
      .toPromise()
    return Promise.all([basisObservable, collateralObservable]).then(
      ([basisMap, isCollateralMap]) => {
        this.basisMap = basisMap
        this.isCollateralMap = isCollateralMap
        this.setUpBasis()
      },
    )
  }

  async setUpBasis() {
    // flatten the arrays of basis objects into a single array and remove duplicates
    this.basisTypeValues = JSON.parse(
      JSON.stringify(
        Object.values(this.basisMap)
          .reduce(
            (accumulator, currentValue) => [...accumulator, ...currentValue],
            [],
          )
          .reduce((accumulator: Basis[], currentValue: Basis) => {
            if (
              !accumulator.some(
                accountBasis => accountBasis.basisId === currentValue.basisId,
              )
            ) {
              accumulator.push(currentValue)
            }
            return accumulator
          }, []),
      ),
    )
    this.selectAllForDropdownItems(this.basisTypeValues)
    this.selectedBasisPrefix.patchValue(
      this.basisTypeValues.map(basis => basis.prefix),
    )
    this.basisTypeValues.forEach((accountBasis, index) => {
      const applicableAccounts = this.findKeysWithBasisPrefix(
        accountBasis.prefix,
      )
      this.initialBasisIndexMap.set(accountBasis.prefix, index)
      this.colDefs = this.colDefs.concat(
        this.addBasisColumn(accountBasis.prefix, applicableAccounts),
      )
    })

    this.gridApi?.api.setColumnDefs(this.colDefs)
    if (this.gridApi) {
      await this.savedViewService.manageSavedView(
        this.gridApi,
        'Bulk-load-workspace-key',
        this.destroySubject$,
      )
      this.gridApi.api.setSortModel(null)
    }
  }

  addBasisColumn(
    basisPrefix: string,
    applicableAccounts: number[],
  ): ColGroupDef {
    return {
      headerName: `${basisPrefix} Details`,
      groupId: basisPrefix,
      openByDefault: true,
      children: [
        {
          field: 'collapsedMarker',
          headerName: 'Expand to Edit Basis Details...',
          width: 200,
          columnGroupShow: 'closed',
        },
        {
          field: `${basisPrefix}OrigTradeDate`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Date,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigSettleDate`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Date,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigCost`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigPrice`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigAccruedInt`,
          headerName: `${basisPrefix} Accrued`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortStartCost`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortStartPrice`,
          type: getInlineEditColumnType(),
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          columnGroupShow: 'open',
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortStartDate`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Date,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}AmortOverride`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Select,
            options: {
              items: amortOverrideTypesVal?.map(a => a.name),
            },
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OrigYield`,
          headerName: `${basisPrefix} Yield`,
          editable: false,
          columnGroupShow: 'open',
        },
        {
          field: `${basisPrefix}AccumulatedOtti`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} Default Accumulated OTTI`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,

          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}DaysImpaired`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}DerivedAmortStartPrice`,
          hide: true,
        },
        {
          field: `${basisPrefix}DerivedAmortStartCost`,
          hide: true,
        },
        {
          field: 'TaxDaysHeld',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXAccruedMarketDiscount',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXWashSaleDeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxOrigCost`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Original Cost`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxBookValueAtLoad`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Book Value at Load`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxAccruedAtLoad`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Accrued at Load`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}LocalAccruedAtLoad`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} Accrued at Load`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}FxAccumulatedOtti`,
          type: getInlineEditColumnType(),
          headerName: `${basisPrefix} FX Default Accumulated OTTI`,
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXFxAccruedMarketDiscount',
          type: getInlineEditColumnType(),
          headerName: 'TAX FX Accrued Market Discount',
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXFxWashSaleDeferredLoss',
          type: getInlineEditColumnType(),
          headerName: `TAX FX Wash Sale Deferred Loss`,
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}Commission`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}OriginalOriginationFee`,
          headerName: `${basisPrefix} Original Origination Fee`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        this.getFeeBasisColDef(
          `${basisPrefix}ExitFee`,
          `${basisPrefix} Exit Fee`,
          applicableAccounts,
        ),
        this.getFeeBasisColDef(
          `${basisPrefix}ExitFeeReceivable`,
          `${basisPrefix} Exit Fee Receivable`,
          applicableAccounts,
        ),
        this.getFeeBasisColDef(
          `${basisPrefix}DrawFee`,
          `${basisPrefix} Draw Fee`,
          applicableAccounts,
        ),
        this.getFeeBasisColDef(
          `${basisPrefix}PrepaymentPenaltyFee`,
          `${basisPrefix} Prepayment Penalty Fee`,
          applicableAccounts,
        ),
        {
          field: 'TAXStraddleDeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXFxStraddleDeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXSection1256DeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: 'TAXFxSection1256DeferredLoss',
          type: getInlineEditColumnType(),
          hide: basisPrefix != 'TAX',
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipValueGetter: (params: ITooltipParams) => {
            return this.toolTipValueGetter(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}PrepaidInterest`,
          headerName: `${basisPrefix} Prepaid Interest`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}InterestDue`,
          headerName: `${basisPrefix} Past Due Interest`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        ...createBulkLoadBasisLoanFieldColumns(
          basisPrefix,
          applicableAccounts,
          {
            fieldPath: field => this.concatToCamelCase(basisPrefix, field),
            getCellStyle: params => this.getCellStyle(params),
            showToolTip: params => this.showToolTip(params),
          },
        ),
        {
          field: `${basisPrefix}LpCumulativeEarnings`,
          headerName: `${basisPrefix} LP Cumulative Earnings`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}LpCumulativeDistributions`,
          headerName: `${basisPrefix} LP Cumulative Distributions`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}ReturnOfInvestment`,
          headerName: `${basisPrefix} Return of Investment`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
        {
          field: `${basisPrefix}ReturnOnInvestment`,
          headerName: `${basisPrefix} Return on Investment`,
          type: getInlineEditColumnType(),
          cellRendererParams: {
            editButtonClickOnly: true,
          } as InlineEditDisplayOptions,
          cellEditorParams: {
            dataType: InlineEditDataType.Number,
          },
          cellStyle: (params: CellClassParams) => {
            return this.getCellStyle(params)
          },
          tooltipComponentParams: {
            showTooltip: params => this.showToolTip(params),
          } as NfTooltipParams,
          editable: (params: EditableCallbackParams) =>
            applicableAccounts.includes(params.data.accountId),
          columnGroupShow: 'open',
          comparator: customSortComparatorBulkNumeric,
          valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
        },
      ],
    }
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => {
        this.gridApi = event
        this.pasteService.registerGrid(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.bulkDeleteService.registerGrid(event.api)
      },
      getRowNodeId: ({ rowId }: BulkLoadRow) => rowId,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: false,
      },
      sideBar: false,
      rowSelection: 'multiple',
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandler(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: this.currentRowIndex,
            colKey: event.column.getColId(),
          })
        }
      },

      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
      suppressMultiRangeSelection: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      enableFillHandle: true,
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.colDefs,
      tooltipShowDelay: 0,
      onSortChanged: (params: SortChangedEvent) => onSortChange(params),
      onCellFocused: (event: CellFocusedEvent) => {
        this.handleCellFocus(event)
      },
    }
  }

  handleCellFocus(event: CellFocusedEvent) {
    this.currentRowIndex = event.rowIndex ?? 0
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return [
      {
        name: 'Remove Row(s)',
        tooltip: 'Remove selected row(s)',
        action: () => {
          this.bulkDeleteService.deleteHandler()
        },
      },
      'export',
    ]
  }

  orderIdCol() {
    const orderIdCol: ColDef = {
      field: loadField('orderId'),
      headerName: 'Order ID',
      comparator: customSortComparatorBulkNumeric,
    }
    if (this.transInput?.transactions?.[0].orderId) return [orderIdCol]
    else return []
  }

  private createColumnDefs(): (ColDef | ColGroupDef)[] {
    const colDefs: (ColDef | ColGroupDef)[] = [
      {
        field: loadField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: (params: { data: { validityStatus: string } }) =>
          deduceValidityStatusCellClass(params.data.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessageMap &&
            params.data.errorMessageMap.size > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
        tooltipComponent: ErrorTooltipComponent,
        comparator: customSortComparatorBulkValidatorColumn,
      },
      {
        field: loadField('rowNumber'),
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
        sortable: false,
      },
      {
        field: loadField('errorMessageMap'),
        hide: true,
      },
      {
        headerName: 'Transaction Details',
        groupId: 'TransactionDetails',
        children: [
          ...this.orderIdCol(),
          {
            field: loadField('legacyOrigTranId'),
            headerName: 'Orig Transaction ID',
            columnGroupShow: 'open',
            comparator: customSortComparatorBulkNumeric,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
          },
          {
            field: loadField('accountId'),
            headerName: 'Account ID',
            width: 100,
            comparator: customSortComparatorBulkNumeric,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
          },
          {
            field: loadField('tranType'),
            headerName: 'Tran Type',
            width: 100,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
          },
          {
            field: loadField('assetId'),
            width: 120,
            headerName: 'Asset ID',
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
          },
          {
            field: loadField('smType'),
            headerName: 'Sm Type',
            width: 100,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
          },
          {
            field: loadField('entryDate'),
            headerName: 'Load Date',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Date,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
          },
          {
            field: loadField('units'),
            headerName: 'Orig Units',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            comparator: customSortComparatorBulkNumeric,
          },
          {
            field: loadField('factor'),
            headerName: 'Factor',
            width: 100,
            columnGroupShow: 'open',
            comparator: customSortComparatorBulkNumeric,
          },
          {
            field: loadField('currentUnits'),
            width: 100,
            headerName: 'Cur Units',
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
            },
            columnGroupShow: 'open',
            comparator: customSortComparatorBulkNumeric,
          },
          {
            field: loadField('transferPrice'),
            headerName: 'Transfer Price',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
              disablePopover: true,
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            comparator: customSortComparatorBulkNumeric,
          },
          {
            field: loadField('marketValue'),
            headerName: 'Market Value',
            width: 100,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Number,
            },
            columnGroupShow: 'open',
          },
          {
            field: loadField('currencyCode'),
            width: 100,
            headerName: 'Currency',
            columnGroupShow: 'open',
            comparator: customSortComparatorBulkNumeric,
          },
          {
            field: loadField('inventoryType'),
            headerName: 'Inventory Type',
            width: 120,
            type: getInlineEditColumnType(),
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellEditorParams: {
              dataType: InlineEditDataType.Select,
              options: {
                items: Object.values(InventoryType),
              },
            },
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
          },
          {
            field: loadField('clientLotId'),
            headerName: 'Client Lot ID',
            width: 100,
            type: getInlineEditColumnType(),
            cellEditorParams: {
              dataType: InlineEditDataType.Number,

              disablePopover: true,
            },
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            comparator: customSortComparatorBulkNumeric,
          },
          {
            field: loadField('counterPartyId'),
            headerName: 'Counterparty ID',
            width: 80,
            type: getInlineEditColumnType(),
            cellEditorParams: {
              dataType: InlineEditDataType.Number,

              disablePopover: true,
            },
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            comparator: customSortComparatorBulkNumeric,
          },
          {
            field: loadField('collateralBroker'),
            headerName: 'Collateral Broker',
            width: 100,
            type: getInlineEditColumnType(),
            cellEditorParams: {
              dataType: InlineEditDataType.String,

              disablePopover: true,
            },
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            editable: (params: EditableCallbackParams) =>
              this.isCollateralMap
                ? this.isCollateralMap[params.data.accountId]
                : false,
          },
          {
            field: loadField('prepaidInterest'),
            headerName: 'Prepaid Interest',
            width: 100,
            type: getInlineEditColumnType(),
            cellEditorParams: {
              dataType: InlineEditDataType.Number,

              disablePopover: true,
            },
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            editable: true,
          },
          {
            field: loadField('interestDue'),
            headerName: 'Past Interest Due',
            width: 100,
            type: getInlineEditColumnType(),
            cellEditorParams: {
              dataType: InlineEditDataType.Number,

              disablePopover: true,
            },
            cellRendererParams: {
              editButtonClickOnly: true,
            } as InlineEditDisplayOptions,
            cellStyle: (params: CellClassParams) => {
              return this.getCellStyle(params)
            },
            tooltipValueGetter: (params: ITooltipParams) => {
              return this.toolTipValueGetter(params)
            },
            tooltipComponentParams: {
              showTooltip: params => this.showToolTip(params),
            } as NfTooltipParams,
            columnGroupShow: 'open',
            editable: true,
          },
          ...createBulkLoadLoanFieldColumns({
            fieldPath: loadField,
            getCellStyle: params => this.getCellStyle(params),
            toolTipValueGetter: params => this.toolTipValueGetter(params),
            showToolTip: params => this.showToolTip(params),
          }),
          {
            field: securityField('transactionBasis'),
            hide: true,
          },
        ],
      },
    ]
    colDefs.push(this.getSecurityDetailsColumnDef())
    return colDefs
  }

  private getSecurityDetailsColumnDef(): ColGroupDef {
    return {
      headerName: 'Security Details',
      openByDefault: true,
      groupId: 'SecurityDetails',
      children: [
        {
          field: 'collapsedMarker',
          headerName: 'Expand to see Security Details...',
          width: 200,
          columnGroupShow: 'closed',
        },
        {
          field: loadField('issueDate'),
          headerName: 'Issue Date',
          width: 100,
          columnGroupShow: 'open',
        },
        {
          field: loadField('maturityDate'),
          headerName: 'Maturity Date',
          width: 100,
          columnGroupShow: 'open',
        },
        {
          field: loadField('ticker'),
          headerName: 'Ticker',
          width: 100,
          columnGroupShow: 'open',
        },
        {
          field: loadField('figi'),
          width: 100,
          headerName: 'FIGI',
          columnGroupShow: 'open',
        },
        {
          field: loadField('sedol'),
          width: 100,
          headerName: 'SEDOL',
          columnGroupShow: 'open',
        },
        {
          field: loadField('isin'),
          width: 100,
          headerName: 'ISIN',
          columnGroupShow: 'open',
        },
      ],
    }
  }

  // Flags active rows with no account as invalid client-side so the user gets an
  // inline "Account is required" error on the cell instead of a silently disabled
  // Validate button. Returns true if any row was missing an account, in which case
  // validate() short-circuits and never sends accountId 0 to the backend.
  markMissingAccountRows(): boolean {
    const anyMissing = markRowsMissingAccount(
      this.gridApi?.api,
      'accountId',
      this.deduceImpactedField('accountId'),
      this.cellLevelErrorMessageMap,
    )
    if (anyMissing) {
      this.isDataValid = false
    }
    return anyMissing
  }

  async deriveAndValidate() {
    if (this.markMissingAccountRows()) {
      return
    }
    this.errorDetails = ''
    await this.populateDerivedFields()
    await this.validate()
  }

  async populateDerivedFields() {
    this.isDeriving = true
    this.loadingText = TransactionConstants.FETCH_DERIVED_FIELDS
    try {
      const derivedResponse = await this.bulkTransactionService
        .getDerivedFieldsForBulkLoad({
          tranData: this.getGridTransactionsAndTpiForDerived(),
          accountBases: this.basisMap,
        })
        .toPromise()
      // PVAL-26104 (BC-11b): reset-then-apply for the derive-only amount slot.
      // The loop below iterates the *response*, so a row the backend omits is
      // never visited and would silently keep the previous cycle's derived
      // amount. Clearing the slot on every row first means `derivedAmount` can
      // only ever hold a value the current derive actually returned, so a stale
      // proceeds amount can never reach the save command.
      this.rowData.forEach(row => {
        row.derivedAmount = undefined
      })
      new Map(Object.entries(derivedResponse)).forEach((value, key) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const currentRow: BulkLoadRow | any = this.rowData.find(
          row => row.transactionId == key,
        )
        if (currentRow) {
          if (value.transactionDerivedFields.transferPrice) {
            currentRow.transferPrice =
              value.transactionDerivedFields.transferPrice
          }
          if (value.transactionDerivedFields.factor) {
            currentRow.factor = value.transactionDerivedFields.factor
          }
          if (value.transactionDerivedFields.marketValue) {
            currentRow.marketValue = value.transactionDerivedFields.marketValue
          }
          if (value.transactionDerivedFields.currentUnits) {
            currentRow.currentUnits =
              value.transactionDerivedFields.currentUnits
          }
          // PVAL-26104: capture the server-derived proceeds amount so it can be
          // carried into the save command. The backend authors this value for a
          // cash-affecting load; the UI only threads it through.
          currentRow.derivedAmount = value.transactionDerivedFields.amount
          const tpiMap = new Map(Object.entries(value.tpiDerivedFields))
          tpiMap.forEach((tpiDerivedFields, tpiBasisId) => {
            const basisPrefix = this.basisTypeValues.find(
              basis => basis.basisId == Number(tpiBasisId),
            )?.prefix

            if (tpiDerivedFields.amortStartPrice != undefined) {
              ;(currentRow[
                `${basisPrefix}DerivedAmortStartPrice` as keyof typeof currentRow
              ] as unknown as number) = tpiDerivedFields.amortStartPrice
            } else {
              currentRow[
                `${basisPrefix}DerivedAmortStartPrice` as keyof typeof currentRow
              ] = undefined
            }

            if (tpiDerivedFields.amortStartCost != undefined) {
              ;(currentRow[
                `${basisPrefix}DerivedAmortStartCost` as keyof typeof currentRow
              ] as unknown as number) = tpiDerivedFields.amortStartCost
            } else {
              currentRow[
                `${basisPrefix}DerivedAmortStartCost` as keyof typeof currentRow
              ] = undefined
            }

            if (
              tpiDerivedFields.amortStartPrice != undefined &&
              (!currentRow[
                `${basisPrefix}AmortStartPrice` as keyof typeof currentRow
              ] as unknown as number)
            ) {
              ;(currentRow[
                `${basisPrefix}AmortStartPrice` as keyof typeof currentRow
              ] as unknown as number) = tpiDerivedFields.amortStartPrice
            }

            if (
              tpiDerivedFields.amortStartCost != undefined &&
              (!currentRow[
                `${basisPrefix}AmortStartCost` as keyof typeof currentRow
              ] as unknown as number)
            ) {
              ;(currentRow[
                `${basisPrefix}AmortStartCost` as keyof typeof currentRow
              ] as unknown as number) = tpiDerivedFields.amortStartCost
            }

            if (
              tpiDerivedFields.origCost &&
              (!currentRow[
                `${basisPrefix}OrigCost` as keyof typeof currentRow
              ] as unknown as number)
            ) {
              ;(currentRow[
                `${basisPrefix}OrigCost` as keyof typeof currentRow
              ] as unknown as number) = tpiDerivedFields.origCost
            }

            if (
              tpiDerivedFields.origPrice &&
              (!currentRow[
                `${basisPrefix}OrigPrice` as keyof typeof currentRow
              ] as unknown as number)
            ) {
              ;(currentRow[
                `${basisPrefix}OrigPrice` as keyof typeof currentRow
              ] as unknown as number) = tpiDerivedFields.origPrice
            }
            if (tpiDerivedFields.origYield) {
              ;(currentRow[
                `${basisPrefix}OrigYield` as keyof typeof currentRow
              ] as unknown as number) = tpiDerivedFields.origYield ?? 0
            }
          })
        }
      })
      this.gridApi?.api.applyTransaction({ update: [this.rowData] })
      this.isDeriving = false
    } catch (error) {
      this.errorHeader = TransactionConstants.ERROR_FETCH_DERIVED_FIELDS
      this.errorDetails = error.error
      this.isDeriving = false
    }
  }

  getGridTransactionsAndTpiForDerived(): BulkDerivedRequestEntry[] {
    const transactionsAndTpi: BulkDerivedRequestEntry[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactionsAndTpi.push(
          this.prepareBulkDerivedRequestData(rowNode.data),
        )
    })
    return transactionsAndTpi
  }

  prepareBulkDerivedRequestData(rowData: BulkLoadRow): BulkDerivedRequestEntry {
    const cp = this.counterPartyList.find(
      counterParty => counterParty.id == rowData.counterPartyId,
    )
    const originalSettleDate = rowData[
      'GAAPOrigSettleDate' as keyof typeof rowData
    ] as string
    let realSettle = null
    if (originalSettleDate && rowData.entryDate !== '') {
      realSettle =
        compareAsc(
          parseISO(rowData.entryDate),
          parseISO(originalSettleDate),
        ) === -1
          ? originalSettleDate
          : rowData.entryDate
    } else if (originalSettleDate) {
      realSettle = originalSettleDate
    } else if (rowData.entryDate !== '') {
      realSettle = rowData.entryDate
    }

    const dataMap: { [key: string]: string | number | undefined } = {
      collateralBroker: rowData.collateralBroker,
    }

    if (rowData.prepaidInterest) {
      dataMap['prepaidInterest'] = Number(rowData.prepaidInterest)
    }
    if (rowData.interestDue) {
      dataMap['interestDue'] = Number(rowData.interestDue)
    }
    Object.assign(
      dataMap,
      getLoanFieldValues(field => rowData[field]),
    )

    const transaction: BulkLoadTransactionDerived = {
      legacyOrigTranId: rowData.legacyOrigTranId,
      transactionId: rowData.transactionId,
      legacyTranId: rowData.legacyTranId,
      accountId: rowData.accountId,
      tranType: 'INITIAL_LOAD',
      units: rowData.units !== '' ? rowData.units : undefined,
      entryDate: rowData.entryDate !== '' ? rowData.entryDate : null,
      cusip: rowData.assetId,
      securityId: rowData.securityId,
      factor: rowData.factor,
      marketValue: rowData.marketValue !== '' ? rowData.marketValue : undefined,
      transferPrice:
        rowData.transferPrice !== '' ? rowData.transferPrice : undefined,
      counterParty: cp ?? getDefaultUnknownCounterParty(),
      counterPartyId:
        rowData.counterPartyId ?? getDefaultUnknownCounterParty().id,
      inventoryType:
        Object.keys(InventoryType)[
          Object.values(InventoryType).indexOf(
            rowData.inventoryType as InventoryType,
          )
        ],
      dataMap: dataMap,
      settleDate: realSettle,
      postDate: realSettle,
    }

    const tpiDataMap: { [key: string]: TransferPurchaseInfo } = {}
    this.basisTypeValues.forEach((basisCurrent: Basis) => {
      const tpiRowMap: TransferPurchaseInfo = this.createTpiForBasisAndRow(
        basisCurrent.prefix,
        rowData,
      )
      if (tpiRowMap && this.tpiHasSomeData(tpiRowMap)) {
        this.plugEssentialInvisibleData(
          tpiRowMap,
          rowData,
          basisCurrent.basisId,
        )
        tpiDataMap[basisCurrent.basisId] = tpiRowMap
      }
    })

    return { transaction, tpiDataMap }
  }

  tpiHasSomeData(tpiRowMap: TransferPurchaseInfo): boolean {
    let tpiProp: keyof typeof tpiRowMap
    for (tpiProp in tpiRowMap) {
      if (tpiProp != 'basisId' && tpiProp != 'id') {
        if (
          tpiRowMap[tpiProp] &&
          tpiRowMap[tpiProp] !== null &&
          tpiRowMap[tpiProp] !== ''
        ) {
          return true
        }
      }
    }
    return false
  }
  async validate() {
    this.isDataValid = true
    this.isCommonValidationError = false
    if (this.markMissingAccountRows()) {
      return
    }
    const bulkValidateView = this.createBulkValidationView()
    this.isValidating = true
    try {
      this.loadingText = TransactionConstants.VALIDATE_TRANSACTION
      const validationInfos = await this.bulkTransactionService
        .validateBulkTransaction(bulkValidateView, TranCategoryUrl.TRANSFER)
        .toPromise()
      this.handleBulkValidationResponse(validationInfos.validationMap)
      this.handleCommonValidations(validationInfos.commonValidations)
      this.allDataValidated = true
      this.disableSaveValidRowsButton()
      this.isValidating = false
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  handleCommonValidations(commonValidations: ValidationInfo[]) {
    this.genericErrors = []
    this.genericWarnings = []
    this.currentNoteError = null
    if (commonValidations.length == 0) {
      this.showConfirmButtonModal = false
    }
    commonValidations.forEach(validationInfo => {
      if (validationInfo.impactedFields.length == 0) {
        validationInfo.impactedFields = ['generic']
      }
      validationInfo.impactedFields.forEach(field => {
        if (field == 'transactionNote') {
          this.isDataValid = false
          this.isCommonValidationError = true
          const validatorKey = 'transactionNoteValidator'
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.note.markAsTouched()
            this.currentNoteError = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
            this.note.setErrors(this.currentNoteError)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.noteWarning = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
          }
        } else if (
          field == 'confirmButton' &&
          validationInfo.level == ErrorSeverity.WARN
        ) {
          this.showConfirmButtonModal = true
        } else {
          //generic case
          this.isCommonValidationError = true
          this.isDataValid = false
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.genericErrors.push(validationInfo.message)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.genericWarnings.push(validationInfo.message)
          }
        }
      })
    })
  }

  toolTipValueGetter(params: ITooltipParams): string {
    const impactedFieldColDef = params.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (currentValidationErrors) {
      return currentValidationErrors.join(' | ')
    } else if (currentValidationWarnings) {
      return currentValidationWarnings.join(' | ')
    } else {
      return params.value
    }
  }

  getCellStyle(params: CellClassParams): CellStyle {
    const impactedFieldProcessed = this.deduceImpactedField(
      params.colDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    if (this.cellLevelErrorMessageMap.has(cellKey)) {
      return { 'background-color': 'lightcoral' }
    } else if (this.cellLevelWarningMessageMap.has(cellKey)) {
      return { 'background-color': 'yellow' }
    } else {
      return { 'background-color': '#00000000' }
    }
  }

  handleBulkValidationResponse(bulkValidationInfos: {
    [key: number]: ValidationInfo[]
  }) {
    // create errorMessages column per row and update tooltip with it

    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        rowNode.data.validityStatus = 'valid'
        rowNode.data.errrorMessageMap = new Map()
      }
    })

    this.note.setErrors(null)
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    const bulkValidationInfo = new Map<string, ValidationInfo[]>(
      Object.entries(bulkValidationInfos),
    )
    bulkValidationInfo.forEach((rowValidationInfo: ValidationInfo[], index) => {
      // create a map of key index,field and keep all errors(string or string[](if erros ) and warnings (if onlky warnings)
      // in cell class rule make it yellow if warning validity status
      // in tooltip use above map to put all erros in tooltip
      const currentRowNode = this.gridApi?.api.getRowNode(index)
      const errorMessages = new Map<string, string[]>()
      const warningMessages = new Map<string, string[]>()

      rowValidationInfo.forEach((validationInfo: ValidationInfo) => {
        let validationMessage = validationInfo.message
        let basisPrefix: string | undefined = undefined
        const basisMatch = validationMessage.match(
          /For BasisId : (\d+) : (.*$)/,
        )

        if (basisMatch) {
          basisPrefix = this.basisTypeValues.find(
            basis => basis.basisId == parseInt(basisMatch[1]),
          )?.prefix

          validationMessage = basisMatch[2]
        }

        if (validationInfo.level == ErrorSeverity.ERROR) {
          validationInfo.impactedFields.forEach((impactedField: string) => {
            const deducedImpactedField = this.deduceImpactedField(
              impactedField,
              basisPrefix,
            )
            const currentKey = `${index}-${deducedImpactedField}`
            if (this.cellLevelErrorMessageMap.has(currentKey)) {
              this.cellLevelErrorMessageMap
                .get(currentKey)
                ?.push(validationMessage)
              errorMessages
                .get(this.deduceImpactedField(impactedField, basisPrefix))
                ?.push(validationMessage)
            } else {
              this.cellLevelErrorMessageMap.set(currentKey, [validationMessage])
              errorMessages.set(
                this.deduceImpactedField(impactedField, basisPrefix),
                [validationMessage],
              )
            }
          })
        } else {
          validationInfo.impactedFields.forEach((impactedField: string) => {
            const deducedImpactedField = this.deduceImpactedField(
              impactedField,
              basisPrefix,
            )
            const currentKey = `${index}-${deducedImpactedField}`
            if (this.cellLevelWarningMessageMap.has(currentKey)) {
              this.cellLevelWarningMessageMap
                .get(currentKey)
                ?.push(validationMessage)
              warningMessages
                .get(this.deduceImpactedField(impactedField, basisPrefix))
                ?.push(validationMessage)
            } else {
              this.cellLevelWarningMessageMap.set(currentKey, [
                validationMessage,
              ])
              warningMessages.set(
                this.deduceImpactedField(impactedField, basisPrefix),
                [validationMessage],
              )
            }
          })
        }
      })
      if (errorMessages.size > 0 && currentRowNode) {
        this.isDataValid = false
        currentRowNode.data.validityStatus = 'invalid'
        currentRowNode.data.errorMessageMap = errorMessages
      }
      if (
        errorMessages.size == 0 &&
        warningMessages.size > 0 &&
        currentRowNode
      ) {
        currentRowNode.data.validityStatus = 'warning'
        currentRowNode.data.errorMessageMap = warningMessages
      }

      if (rowValidationInfo.length == 0 && currentRowNode) {
        currentRowNode.data.validityStatus = 'valid'
        currentRowNode.data.errorMessageMap = new Map()
      }
    })
    this.gridApi?.api.redrawRows()
  }

  deduceImpactedField(
    impactedFieldFromValidationInfo: string,
    basisPrefix?: string,
  ): string {
    if (basisPrefix) {
      return this.concatToCamelCase(
        basisPrefix,
        impactedFieldFromValidationInfo,
      )
    }
    if (impactedFieldFromValidationInfo == 'securityId') {
      return 'assetId'
    } else {
      return impactedFieldFromValidationInfo
    }
  }

  createBulkValidationView(): BulkLoadValidationView {
    const view: BulkLoadValidationView = {
      transaction: this.getGridTransactions(true),
      requestMode: RequestMode.EDIT_TRAN,
      reconDate: this.reconDate ?? new Date(),
      tranCategory: TranCategory.TRANSFER,
    }
    if (this.note.value !== '') view.transactionNote = this.note.value
    return view
  }

  getGridOnlyValidTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus == 'valid' ||
        rowNode.data.validityStatus == 'warning'
      )
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }

  getGridTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }

  prepareBulkTransactionData(
    isValidate: boolean,
    rowData: BulkLoadRow,
  ): TransactionRequestWrapper {
    const currentOriginalTransaction = this.originalTransactions.find(
      origTran => origTran.legacyOrigTranId == rowData.legacyOrigTranId,
    )
    const cp = this.counterPartyList.find(
      counterParty => counterParty.id == rowData.counterPartyId,
    )
    let createdDate = new Date().toISOString()
    if (currentOriginalTransaction?.createdDate) {
      createdDate = new Date(
        currentOriginalTransaction?.createdDate,
      ).toISOString()
    }
    const originalSettleDate = rowData[
      'GAAPOrigSettleDate' as keyof typeof rowData
    ] as string
    let realSettle = rowData.entryDate
    if (originalSettleDate) {
      realSettle =
        compareAsc(
          parseISO(rowData.entryDate),
          parseISO(originalSettleDate),
        ) === -1
          ? originalSettleDate
          : rowData.entryDate
    }

    const transactionData: BulkLoadTransaction = {
      tranType: 'INITIAL_LOAD',
      userId: this.userId ?? 0,
      accountId: rowData.accountId,
      entryDate: rowData.entryDate,
      securityId: rowData.securityId,
      counterParty: cp ?? getDefaultUnknownCounterParty(),
      counterPartyId:
        rowData.counterPartyId ?? getDefaultUnknownCounterParty().id,
      currUnits: rowData.currentUnits !== '' ? rowData.currentUnits : undefined,
      clientLotId: rowData.clientLotId,
      settleDate: realSettle,
      postDate: realSettle,
    }
    transactionData.dataMap = {}

    if (rowData.collateralBroker) {
      transactionData.dataMap.collateralBroker = rowData.collateralBroker
    }

    if (rowData.prepaidInterest) {
      transactionData.dataMap.prepaidInterest = Number(rowData.prepaidInterest)
    }

    if (rowData.interestDue) {
      transactionData.dataMap.interestDue = Number(rowData.interestDue)
    }

    Object.assign(
      transactionData.dataMap,
      getLoanFieldValues(field => rowData[field]),
    )

    if (this.initialClientLotIdMap.get(rowData.transactionId)) {
      transactionData.dataMap.clientLotId = this.initialClientLotIdMap.get(
        rowData.transactionId,
      )
    }

    if (rowData.transferPrice !== '') {
      transactionData.transferPrice = rowData.transferPrice
    }
    if (rowData.units !== '') {
      transactionData.units = rowData.units
    }
    if (rowData.inventoryType !== '') {
      transactionData.inventoryType =
        Object.keys(InventoryType)[
          Object.values(InventoryType).indexOf(
            rowData.inventoryType as InventoryType,
          )
        ]
    }

    if (!isValidate) {
      transactionData.revisionId = currentOriginalTransaction?.revisionId
        ? currentOriginalTransaction.revisionId + 1
        : 1
      transactionData.sourceId = TransactionConstants.HELIOS_SOURCE_ID
      transactionData.createdDate = createdDate
      transactionData.modifiedDate = new Date().toISOString()
      transactionData.active = true
      transactionData.oldClientLotId = this.initialClientLotIdMap.get(
        rowData.transactionId,
      )
      transactionData.clearedDate = rowData.clearedDate
    }

    if (currentOriginalTransaction) {
      transactionData.transactionId = currentOriginalTransaction.transactionId
      transactionData.legacyOrigTranId =
        currentOriginalTransaction.legacyOrigTranId
      transactionData.legacyTranId = currentOriginalTransaction.legacyTranId
    }

    // PVAL-26104: a cash-affecting load must carry its proceeds as the
    // transaction amount. The bulk mapper never populated `amount`, so every
    // "Helios Bulk Load Dialog Update" persisted a zero cash amount. The amount
    // is authored server-side (SecurityTransferService derives it and returns it
    // on the derivedFields response); here we only carry that server-derived
    // value into the save command — the UI never recomputes the formula.
    //
    // Resolution order, and why omission is NOT a safe default: the Edit
    // Transaction write path replaces the persisted amount from the payload, so
    // a command that omits `amount` zeroes the cash leg. That is precisely the
    // reported defect — the pre-fix command carried no `amount` field at all and
    // −891,000 became 0, then stayed 0 across further untouched re-saves. So
    // when the current derive cycle produced no amount we fall back to the
    // amount the transaction being edited already carries, which makes an
    // untouched re-save round-trip its cash amount instead of zeroing it.
    // `??` and never `||`, so a server-derived 0 still wins over the existing
    // value — a truthy check here is the same class of bug as the omission.
    // Only when neither slot is populated (a brand-new row with no original
    // transaction and no derive) is the field left off, unchanged from before.
    //
    // The trade this makes, stated explicitly: the fallback is the PRE-EDIT
    // amount, so it is correct only while the derive authors `amount` for every
    // cash-affecting row it visits. If a user edits units/price and the derive
    // response visits that row but omits `amount`, this re-sends the pre-edit
    // value instead of the (unknown) new one. The normal edit -> derive -> save
    // flow does not hit that: an edit marks the row intermediate and the derive
    // re-runs, resetting every `derivedAmount` and re-applying the fresh
    // response. It is still strictly safer than the omission it replaces, which
    // zeroed the leg outright rather than keeping a possibly-stale value.
    const resolvedAmount =
      rowData.derivedAmount ?? currentOriginalTransaction?.amount
    if (resolvedAmount !== undefined) {
      transactionData.amount = resolvedAmount
    }

    const metaData: MetaDataTransaction = {
      rowId: Number(rowData.rowId),
      reconDate: this.reconDate ?? new Date(),
      cusip: rowData.assetId,
      tpis: this.getRowTpis(rowData),
      basis: rowData.transactionBasis,
    }

    return { transactionData, metaData }
  }

  getRowTpis(rowData: BulkLoadRow): TransferPurchaseInfo[] {
    const tpis: TransferPurchaseInfo[] = []
    this.basisTypeValues.forEach((basis: Basis) => {
      const tpiRowMap: TransferPurchaseInfo = this.createTpiForBasisAndRow(
        basis.prefix,
        rowData,
      )
      if (tpiRowMap && this.tpiHasSomeData(tpiRowMap)) {
        this.plugEssentialInvisibleData(tpiRowMap, rowData, basis.basisId)
        tpis.push(tpiRowMap)
      }
    })
    return tpis
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    const rowNode = event.node
    const colId = event.column.getColId()

    if (
      colId != 'validityStatus' &&
      colId != 'errorMessageMap' &&
      rowNode.data.validityStatus != 'intermediate'
    ) {
      rowNode.data.validityStatus = 'intermediate'
      this.allDataValidated = false
    }
  }

  disableSaveValidRowsButton() {
    let disable = true

    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus == 'valid' ||
        node.data.validityStatus == 'warning'
      ) {
        disable = false // If at least one node has validity 'valid' or 'warning', set disable to false
        return // Exit the loop early if validity is 'valid'
      }
    })

    // Set disableSaveValidRows based on the value of disable
    this.disableSaveValidRows = disable
  }

  validateAndSave(isSaveValidRows?: boolean) {
    if (this.showConfirmButtonModal) {
      const modalRef = this.modalService.open(
        BulkTransactionsSaveConfirmationModalComponent,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          return isSaveValidRows ? this.saveValidRows() : this.save()
        } else return
      })
    } else if (isSaveValidRows) this.saveValidRows()
    else this.save()
  }

  async saveValidRows() {
    await this.save(true)
    const newRowData: (BulkSecurityTransferRow & unknown)[] = []
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'valid' &&
        node.data.validityStatus != 'warning'
      )
        newRowData.push(node.data)
    })
    this.gridApi?.api.setRowData(newRowData)

    this.disableSaveValidRowsButton()
  }

  async save(onlyValidRows?: boolean) {
    if (!this.isDataValid && !onlyValidRows) {
      return
    }
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const tranCommand: BulkTransactionRequest = {
      transaction: onlyValidRows
        ? this.getGridOnlyValidTransactions(false)
        : this.getGridTransactions(false),
      tranCategory: TranCategory.TRANSFER,
      requestMode: RequestMode.EDIT_TRAN,
    }
    if (this.note.value !== '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    tranCommand.notes = TransactionConstants.HELIOS_BULK_LOAD_SAVE_NOTE_CW_EDIT
    this.bulkTransactionService
      .editBulkTransaction(tranCommand, TranCategoryUrl.TRANSFER)
      .subscribe(
        () => {
          this.toastService.success(
            'Bulk Transaction edit command successfully submitted.',
          )
          this.formSubmitting = false
          if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
        },
        error => {
          this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
          this.errorDetails = error.error
          this.formSubmitting = false
        },
      )
  }

  setNoteErrors() {
    if (this.currentNoteError != null) {
      this.note.setErrors(this.currentNoteError)
    }
  }

  private closeModal(
    success: boolean,
    transactionRequests: TransactionRequestWrapper[],
  ): void {
    this.activeModal.close({
      success,
      transactionRequests,
    } as BulkTransactionModalResponse)
  }

  async loadTransactionDetailBulk(cwTransId: string[], securityId: number[]) {
    const transactionDetail$: Observable<ClearwaterTransactionDetailResponse> =
      this.bulkTransactionService.loadTransactionDetailBulk(
        cwTransId,
        securityId,
        TranCategoryUrl.TRANSFER,
      )
    return Promise.all([transactionDetail$.toPromise()]).then(
      ([transactionDetail]) => {
        if (this.transInput) {
          const unorderedTransactionDetails =
            transactionDetail.transactionDataMap
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const orderedTransactionDetails: { [key: string]: any } = {}

          // Sort the transaction details based on the order of cwTransid
          for (const id of cwTransId) {
            if (unorderedTransactionDetails[id]) {
              orderedTransactionDetails[id] = unorderedTransactionDetails[id]
            }
          }

          this.clearwaterTransactionDetail = orderedTransactionDetails
          this.securityMasterData = new Map<string, SecurityMasterData>(
            Object.entries(transactionDetail.securityMasterDataMap ?? ''),
          )
          this.tpiMapByTransaction = transactionDetail.tpiDataMap ?? {}
        }
      },
    )
  }

  async loadAdditionalDetailBulk(
    legacyOrigIds: number[],
    legacyTranIds: number[],
    securityIds: number[],
    accountIds: number[],
  ) {
    const additionalDetails$: Observable<AdditionalDetailsSecurityTransfer> =
      this.bulkTransactionService.loadAdditionalDetailsTransferBulk(
        legacyOrigIds,
        legacyTranIds,
        securityIds,
        accountIds,
        false,
      )
    return Promise.all([additionalDetails$.toPromise()]).then(
      ([additionalDetails]) => {
        this.securityMasterData = new Map<string, SecurityMasterData>(
          Object.entries(additionalDetails.securityMasterDataMap ?? ''),
        )
        this.clientLotIdMapBulkPanel = new Map<string, string>(
          Object.entries(additionalDetails.clientLotIdMap ?? ''),
        )
        this.tpiMapByTransaction = additionalDetails.tpiDataMap ?? {}
      },
    )
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  onBasisSelectionChange() {
    const basisColumnGroupsShown = this.selectedBasisPrefix.value as string[]
    const basisColumnGroupsHidden = this.basisTypeValues
      .filter(value => !this.selectedBasisPrefix.value.includes(value.prefix))
      .map((basis: Basis) => basis.prefix)

    basisColumnGroupsShown.forEach(basisPrefix => {
      const index = this.colDefs.findIndex(
        colDef => colDef.headerName === `${basisPrefix} Details`,
      )

      if (index == -1) {
        const applicableAccounts = this.findKeysWithBasisPrefix(basisPrefix)
        const basisInitialIndex =
          this.initialBasisIndexMap.get(basisPrefix) ?? 0
        let preMissingCount = 0
        for (let i = 0; i < basisInitialIndex; i++) {
          if (this.removedBasisIndex.has(i)) preMissingCount++
        }
        const basisIndex =
          basisInitialIndex -
          preMissingCount +
          (this.securityDetailsShow.value ? 5 : 4)
        this.colDefs.splice(
          basisIndex,
          0,
          this.addBasisColumn(basisPrefix, applicableAccounts),
        )
        this.removedBasisIndex.delete(basisInitialIndex)
      }
    })
    basisColumnGroupsHidden.forEach(basisPrefix => {
      const index = this.colDefs.findIndex(
        colDef => colDef.headerName === `${basisPrefix} Details`,
      )
      if (index > -1) {
        this.colDefs.splice(index, 1)
        this.removedBasisIndex.add(
          this.initialBasisIndexMap.get(basisPrefix) ?? 0,
        )
      }
    })
    const groupState = this.gridApi?.columnApi.getColumnGroupState()

    this.gridApi?.api.setColumnDefs(this.colDefs)

    if (groupState) this.gridApi?.columnApi.setColumnGroupState(groupState)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAllForDropdownItems(items: any[]) {
    items.forEach(element => {
      element['selectedAllGroup'] = 'selectedAllGroup'
    })
  }

  findKeysWithBasisPrefix(targetBasisPrefix: string): number[] {
    return Object.keys(this.basisMap)
      .map(key => Number(key))
      .filter(key =>
        this.basisMap[key].some(basis => basis.prefix === targetBasisPrefix),
      )
  }

  expandCollapseAllBasis(expandAll: boolean) {
    this.selectedBasisPrefix.value.forEach((basisPrefixSelected: string) => {
      const group =
        this.gridApi?.columnApi.getOriginalColumnGroup(basisPrefixSelected)
      if (group) this.gridApi?.columnApi.setColumnGroupOpened(group, expandAll)
    })
  }

  concatToCamelCase<T extends string, U extends string>(x: T, y: U): string {
    const camelCaseY = y.charAt(0).toUpperCase() + y.slice(1)
    return `${x}${camelCaseY}`
  }

  returnDisIntegratedTpiProperty(
    basisPrefix: string,
    row: BulkLoadRow,
    property: string,
  ): string | number | Map<string, string[]> | null | undefined {
    const rowField = this.concatToCamelCase(
      property == 'taxDaysHeld' ? '' : basisPrefix,
      property,
    ) as keyof typeof row
    return row[rowField]
  }

  createTpiForBasisAndRow(basisPrefix: string, rowData: BulkLoadRow) {
    const tpi: TransferPurchaseInfo = {} as TransferPurchaseInfo
    TpiPropertiesNumeric.forEach(keyNumeric => {
      ;(tpi[keyNumeric as keyof typeof tpi] as number | undefined) =
        this.returnDisIntegratedTpiProperty(
          basisPrefix,
          rowData,
          keyNumeric,
        ) !== ''
          ? Number(
              this.returnDisIntegratedTpiProperty(
                basisPrefix,
                rowData,
                keyNumeric,
              ),
            )
          : undefined
    })
    TpiPropertiesString.forEach(keyString => {
      ;(tpi[keyString as keyof typeof tpi] as string | undefined) =
        this.returnDisIntegratedTpiProperty(
          basisPrefix,
          rowData,
          keyString,
        )?.toString()
    })

    return tpi
  }

  plugEssentialInvisibleData(
    tpiRowMap: TransferPurchaseInfo,
    rowData: BulkLoadRow,
    basisId: number,
  ) {
    if (!tpiRowMap.basisId) tpiRowMap.basisId = basisId
    if (!tpiRowMap.id) tpiRowMap.id = rowData.legacyTranId

    if (!tpiRowMap.factor) tpiRowMap.factor = rowData.factor

    if (!tpiRowMap.origYield) {
      tpiRowMap.origYield = 0.0
      tpiRowMap.amortStartYield = 0.0
    }
    if (tpiRowMap.amortStartDate) {
      tpiRowMap.valuationDate = tpiRowMap.amortStartDate
    }
    if (!tpiRowMap.amortStartDate) {
      tpiRowMap.amortStartDate = tpiRowMap.origSettleDate
      tpiRowMap.valuationDate = tpiRowMap.origSettleDate
    }

    if (!tpiRowMap.amortOverride) {
      tpiRowMap.amortOverride = 'OBEY_ACCOUNT_SECURITY'
    }
  }

  showToolTip(params?: ITooltipParams) {
    const impactedFieldColDef = params?.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params?.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (
      (currentValidationErrors && currentValidationErrors?.length > 0) ||
      (currentValidationWarnings && currentValidationWarnings?.length > 0) ||
      (params?.data.tranType == '' && params?.data.validityStatus != 'inactive')
    ) {
      return true
    }
    return false
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  copyHeaders() {
    const headers = this.gridApi?.columnApi
      .getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'validityStatus')
      .map(col => col.getColDef().headerName)
    let headersTabSeparated = ''
    if (headers) headersTabSeparated = headers.join('\t')

    navigator.clipboard.writeText(headersTabSeparated)
    this.toastService.success('Copied to Clipboard')
  }

  getFeeBasisColDef(
    field: string,
    headerName: string,
    applicableAccounts: number[],
  ): ColDef {
    return {
      field,
      headerName,
      type: getInlineEditColumnType(),
      cellRendererParams: {
        editButtonClickOnly: true,
      } as InlineEditDisplayOptions,
      cellEditorParams: {
        dataType: InlineEditDataType.Number,
      },
      cellStyle: (params: CellClassParams) => {
        return this.getCellStyle(params)
      },
      tooltipComponentParams: {
        showTooltip: params => this.showToolTip(params),
      } as NfTooltipParams,
      editable: (params: EditableCallbackParams) =>
        applicableAccounts.includes(params.data.accountId),
      columnGroupShow: 'open',
      comparator: customSortComparatorBulkNumeric,
      valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
    }
  }
}
